import { computed, ref, watch } from 'vue'
import type { Ref } from 'vue'
import { useIntervalFn } from '@vueuse/core'

// How often the timer re-reads the clock. Sub-second so the displayed second
// flips promptly; the value only changes reactively once per whole second.
const TICK_MS = 250

// Elapsed timer for a running operation. Ticks from a captured start time (not by
// accumulation) so it stays accurate across tab throttling; resets each time `active`
// turns true and stops when it turns false. Returns a formatted `3s` / `1m 05s` label.
// `startedAt` overrides the start with an epoch timestamp, for an operation that began
// before this scope existed (a page reload mid-run); it falls back to now when null.
// Backed by VueUse's useIntervalFn, which pauses/cleans up with the owning scope.
export function useElapsedTimer(
  active: Ref<boolean>,
  startedAt?: Ref<number | null>
): {
  elapsedLabel: Ref<string>
} {
  const elapsedSeconds = ref(0)
  let startedAtMs = 0

  const tick = (): void => {
    elapsedSeconds.value = Math.max(
      0,
      Math.floor((Date.now() - startedAtMs) / 1000)
    )
  }

  const { pause, resume } = useIntervalFn(tick, TICK_MS, { immediate: false })

  watch(
    active,
    (isActive) => {
      if (!isActive) {
        pause()
        return
      }
      startedAtMs = startedAt?.value ?? Date.now()
      tick()
      resume()
    },
    { immediate: true }
  )

  const elapsedLabel = computed((): string => {
    const total = elapsedSeconds.value
    if (total < 60) return `${total}s`

    const minutes = Math.floor(total / 60)
    const seconds = String(total % 60).padStart(2, '0')
    return `${minutes}m ${seconds}s`
  })

  return { elapsedLabel }
}
